import { useState, useEffect, useCallback, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";

import FormField from "components/FormField";
import Checkbox from "components/Checkbox";
import Dropdown from "components/Dropdown";
import Button from "components/Button";
import Message from "components/Message";
import { MultiSelect } from "react-multi-select-component";
import ContactSelectionPanel from "../ContactSelectionPanel";
import CustomerSelectionPanel from "../CustomerSelectionPanel";
import BadgeInput from "../BadgeInput/BadgeInput";
import RuleFormHelpModal from "./RuleFormHelpModal";
import ScheduleModal from "./ScheduleModal";
import TruckAvailabilitySection from "./TruckAvailabilitySection";

import { createRfqNotificationRule, updateRfqNotificationRule, fetchRfqSources, createSchedule, updateSchedule, deleteSchedule, bulkCreateSchedules } from "actions/rfqNotifications";
import rfqNotificationsService from "services/rfqNotifications.service";
import { isValidTimeFormat } from "pages/RfqNotifications/utils/validation";
import { getApiErrorMessage, parseApiError } from "utils/apiErrors";

import styles from "./RuleForm.module.scss";

const US_STATES = [
  { value: "AL", label: "Alabama" },
  { value: "AK", label: "Alaska" },
  { value: "AZ", label: "Arizona" },
  { value: "AR", label: "Arkansas" },
  { value: "CA", label: "California" },
  { value: "CO", label: "Colorado" },
  { value: "CT", label: "Connecticut" },
  { value: "DE", label: "Delaware" },
  { value: "FL", label: "Florida" },
  { value: "GA", label: "Georgia" },
  { value: "HI", label: "Hawaii" },
  { value: "ID", label: "Idaho" },
  { value: "IL", label: "Illinois" },
  { value: "IN", label: "Indiana" },
  { value: "IA", label: "Iowa" },
  { value: "KS", label: "Kansas" },
  { value: "KY", label: "Kentucky" },
  { value: "LA", label: "Louisiana" },
  { value: "ME", label: "Maine" },
  { value: "MD", label: "Maryland" },
  { value: "MA", label: "Massachusetts" },
  { value: "MI", label: "Michigan" },
  { value: "MN", label: "Minnesota" },
  { value: "MS", label: "Mississippi" },
  { value: "MO", label: "Missouri" },
  { value: "MT", label: "Montana" },
  { value: "NE", label: "Nebraska" },
  { value: "NV", label: "Nevada" },
  { value: "NH", label: "New Hampshire" },
  { value: "NJ", label: "New Jersey" },
  { value: "NM", label: "New Mexico" },
  { value: "NY", label: "New York" },
  { value: "NC", label: "North Carolina" },
  { value: "ND", label: "North Dakota" },
  { value: "OH", label: "Ohio" },
  { value: "OK", label: "Oklahoma" },
  { value: "OR", label: "Oregon" },
  { value: "PA", label: "Pennsylvania" },
  { value: "RI", label: "Rhode Island" },
  { value: "SC", label: "South Carolina" },
  { value: "SD", label: "South Dakota" },
  { value: "TN", label: "Tennessee" },
  { value: "TX", label: "Texas" },
  { value: "UT", label: "Utah" },
  { value: "VT", label: "Vermont" },
  { value: "VA", label: "Virginia" },
  { value: "WA", label: "Washington" },
  { value: "WV", label: "West Virginia" },
  { value: "WI", label: "Wisconsin" },
  { value: "WY", label: "Wyoming" }
];

const CANADIAN_PROVINCES = [
  { value: "AB", label: "Alberta" },
  { value: "BC", label: "British Columbia" },
  { value: "MB", label: "Manitoba" },
  { value: "NB", label: "New Brunswick" },
  { value: "NL", label: "Newfoundland and Labrador" },
  { value: "NS", label: "Nova Scotia" },
  { value: "NT", label: "Northwest Territories" },
  { value: "NU", label: "Nunavut" },
  { value: "ON", label: "Ontario" },
  { value: "PE", label: "Prince Edward Island" },
  { value: "QC", label: "Quebec" },
  { value: "SK", label: "Saskatchewan" },
  { value: "YT", label: "Yukon" }
];

const ALL_STATES_WITH_SEPARATORS = [
  { value: "US_SEPARATOR", label: "--- United States ---", disabled: true },
  ...US_STATES,
  { value: "CA_SEPARATOR", label: "--- Canada ---", disabled: true },
  ...CANADIAN_PROVINCES
];

// Helper function to get the full state/province label by value
const getStateLabel = (value) => {
  const allStates = [...US_STATES, ...CANADIAN_PROVINCES];
  const state = allStates.find(state => state.value === value);
  return state ? state.label : value;
};

// Default for "Max Miles Out (air)". The field is required and bounded (0-300)
// by validateForm only when the truck filter is engaged, so this just supplies
// the model default for non-engaged payloads.
const MAX_MILES_OUT_AIR_DEFAULT = 300;

const normalizeMaxMilesOutAir = (raw) => {
  if (raw === "" || raw === null || raw === undefined) {
    return MAX_MILES_OUT_AIR_DEFAULT;
  }
  const value = Number(raw);
  return isNaN(value) ? MAX_MILES_OUT_AIR_DEFAULT : value;
};

// Removes the permission-gated truck fields from a payload base so they are
// only sent when the user holds the truck-availability permission (re-added,
// normalized, by buildTruckPayloadFields). Keeps both write paths consistent.
const omitTruckFields = (data) => {
  const rest = { ...data };
  delete rest.all_active_trucks;
  delete rest.trucks;
  delete rest.max_miles_out_air;
  return rest;
};

// Operation-specific fallbacks shown in the top-of-form banner when the backend
// carries no usable message (Behavior §3).
const UPDATE_RULE_ERROR = "Failed to update notification rule";
const CREATE_RULE_ERROR = "Failed to create notification rule";

// Resolves the top-of-form banner text from a parseApiError() result using the
// Behavior §2/§3 priority ladder: network sentinel → non-field message verbatim
// → the validation-shape join (parsed.message) of the field messages →
// operation fallback. Pure function; callers own setFieldErrors separately.
const resolveBannerMessage = (parsed, isUpdate) => {
  if (parsed.isNetworkError) {
    return parsed.message;
  }
  if (parsed.nonFieldMessage) {
    return parsed.nonFieldMessage;
  }
  if (Object.keys(parsed.fieldErrorLists).length > 0) {
    // No detail/error/non_field_errors reached this branch, so parsed.message
    // is the validation-shape join (", "-joined, server key order) of the
    // usable response texts — the field messages in the normal case — and is
    // non-null because fieldErrorLists is non-empty. Reuse it instead of
    // re-implementing the join, keeping a single source of truth with
    // apiErrors.js (Behavior §2 rule 3).
    return parsed.message;
  }
  return isUpdate ? UPDATE_RULE_ERROR : CREATE_RULE_ERROR;
};

const RuleForm = ({ rule, loading, onSave, onCancel, onTest }) => {
  const [formData, setFormData] = useState({
    id: null,
    name: "",
    is_active: true,
    emails_for_sending_notification: "",
    note_keywords_whitelist: "",
    note_keywords_blacklist: "",
    pick_up_states_only: [],
    delivery_states_only: [],
    match_pick_up_and_delivery_states: false,
    min_total_miles: 0,
    max_total_miles: 99999,
    rfq_sources: [],
    all_truck_types: true,
    allowed_truck_types: [],
    all_customers: false,
    customers: [],
    all_customer_contacts: false,
    customer_contacts: [],
    all_active_trucks: false,
    trucks: [],
    max_miles_out_air: MAX_MILES_OUT_AIR_DEFAULT,
    enable_time_scheduling: false,
    schedules: [],
    additional_subject_line: "",
  });

  const [submitting, setSubmitting] = useState(false);
  const [toggleLoading, setToggleLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});
  const [showHelpModal, setShowHelpModal] = useState(false);
  const [hasUserModifiedContacts, setHasUserModifiedContacts] = useState(false);
  const [hasUserModifiedCustomers, setHasUserModifiedCustomers] = useState(false);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [editingSchedule, setEditingSchedule] = useState(null);
  const [editingDayIndex, setEditingDayIndex] = useState(null);
  const [scheduleLoading, setScheduleLoading] = useState(false);
  const [scheduleSuccess, setScheduleSuccess] = useState("");
  
  // Truck types state
  const [truckSizes, setTruckSizes] = useState([]);
  const [truckSizesLoading, setTruckSizesLoading] = useState(false);
  const [truckSizesError, setTruckSizesError] = useState("");
  const truckSizesAbortRef = useRef(null);

  // Truck-availability state (permission-gated section)
  const [availableTrucks, setAvailableTrucks] = useState([]);
  const [availableTrucksLoading, setAvailableTrucksLoading] = useState(false);
  const [availableTrucksError, setAvailableTrucksError] = useState("");

  const dispatch = useDispatch();
  const { rfqSources, rfqSourcesLoading, ruleDetailsError, rfqSourcesError } = useSelector((state) => state.rfqNotifications);
  const permissions = useSelector((state) => state.permissions);
  const canManageTruckAvailability = Boolean(permissions?.rfq_notification?.manage_rfq_truck_availability);

  useEffect(() => {
    // Fetch RFQ sources when component mounts
    dispatch(fetchRfqSources());
  }, [dispatch]);

  // Fetch the tenant's active trucks for the truck-availability section, only
  // when the user holds the permission that gates the whole section.
  useEffect(() => {
    if (!canManageTruckAvailability) {
      return;
    }

    let cancelled = false;
    const controller = new AbortController();

    const fetchAvailableTrucks = async () => {
      try {
        setAvailableTrucksLoading(true);
        setAvailableTrucksError("");
        const data = await rfqNotificationsService.getAvailableTrucks(controller.signal);
        if (!cancelled) {
          setAvailableTrucks(Array.isArray(data) ? data : []);
        }
      } catch (error) {
        if (cancelled || error.name === 'AbortError' || error.code === 'ERR_CANCELED') return;
        console.error("Failed to fetch available trucks:", error);
        setAvailableTrucksError("Failed to load trucks. Please try again.");
        setAvailableTrucks([]);
      } finally {
        if (!cancelled) {
          setAvailableTrucksLoading(false);
        }
      }
    };

    fetchAvailableTrucks();

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [canManageTruckAvailability]);

  useEffect(() => {
    setHasUserModifiedContacts(false); // Reset flag when rule changes
    setHasUserModifiedCustomers(false);
    if (rule) {
      setFormData({
        id: rule.id,
        name: rule.name || "",
        is_active: rule.is_active !== undefined ? rule.is_active : true,
        emails_for_sending_notification: rule.emails_for_sending_notification 
          ? (typeof rule.emails_for_sending_notification === 'string' 
              ? rule.emails_for_sending_notification
              : Array.isArray(rule.emails_for_sending_notification) 
                ? rule.emails_for_sending_notification.join(",")
                : "")
          : "",
        note_keywords_whitelist: Array.isArray(rule.note_keywords_whitelist)
          ? rule.note_keywords_whitelist.join(",")
          : rule.note_keywords_whitelist || "",
        note_keywords_blacklist: Array.isArray(rule.note_keywords_blacklist)
          ? rule.note_keywords_blacklist.join(",")
          : rule.note_keywords_blacklist || "",
        pick_up_states_only: rule.pick_up_states_only 
          ? (typeof rule.pick_up_states_only === 'string' 
              ? rule.pick_up_states_only.split(',').map(state => state.trim()).filter(state => state)
              : Array.isArray(rule.pick_up_states_only) 
                ? rule.pick_up_states_only 
                : [])
          : [],
        delivery_states_only: rule.delivery_states_only 
          ? (typeof rule.delivery_states_only === 'string' 
              ? rule.delivery_states_only.split(',').map(state => state.trim()).filter(state => state)
              : Array.isArray(rule.delivery_states_only) 
                ? rule.delivery_states_only 
                : [])
          : [],
        match_pick_up_and_delivery_states: rule.match_pick_up_and_delivery_states || false,
        min_total_miles: rule.min_total_miles !== null && rule.min_total_miles !== undefined ? parseInt(rule.min_total_miles) : 0,
        max_total_miles: rule.max_total_miles !== null && rule.max_total_miles !== undefined ? parseInt(rule.max_total_miles) : 99999,
        rfq_sources: rule.rfq_sources 
          ? (Array.isArray(rule.rfq_sources) 
              ? rule.rfq_sources 
              : [])
          : [],
        all_truck_types: rule.all_truck_types !== undefined ? rule.all_truck_types : true,
        allowed_truck_types: rule.allowed_truck_types 
          ? (typeof rule.allowed_truck_types === 'string' 
              ? rule.allowed_truck_types.split(',').map(type => type.trim()).filter(type => type)
              : Array.isArray(rule.allowed_truck_types) 
                ? rule.allowed_truck_types 
                : [])
          : [],
        all_customers: rule.all_customers || false,
        customers: rule.customers
          ? (Array.isArray(rule.customers)
              ? rule.customers.map(c => c?.id ?? c).filter(id => id !== null && id !== undefined)
              : [])
          : [],
        all_customer_contacts: rule.all_customer_contacts || false,
        customer_contacts: rule.customer_contacts
          ? (Array.isArray(rule.customer_contacts)
              ? rule.customer_contacts.map(contact => contact?.id ?? contact).filter(id => id !== null && id !== undefined)
              : [])
          : [],
        all_active_trucks: rule.all_active_trucks || false,
        trucks: rule.trucks
          ? (Array.isArray(rule.trucks)
              ? rule.trucks.map(truck => truck?.id ?? truck).filter(id => id !== null && id !== undefined)
              : [])
          : [],
        max_miles_out_air: rule.max_miles_out_air !== null && rule.max_miles_out_air !== undefined
          ? rule.max_miles_out_air
          : MAX_MILES_OUT_AIR_DEFAULT,
        enable_time_scheduling: rule.schedules && rule.schedules.length > 0,
        schedules: rule.schedules || [],
        additional_subject_line: rule.additional_subject_line || "",
      });
    } else {
      // Reset form for create mode
      setFormData({
        id: null,
        name: "",
        is_active: true,
        emails_for_sending_notification: "",
        note_keywords_whitelist: "",
        note_keywords_blacklist: "",
        pick_up_states_only: [],
        delivery_states_only: [],
        match_pick_up_and_delivery_states: false,
        min_total_miles: 0,
        max_total_miles: 99999,
        rfq_sources: [],
        all_truck_types: true,
        allowed_truck_types: [],
        all_customers: false,
        customers: [],
        all_customer_contacts: false,
        customer_contacts: [],
        all_active_trucks: false,
        trucks: [],
        max_miles_out_air: MAX_MILES_OUT_AIR_DEFAULT,
        enable_time_scheduling: false,
        schedules: [],
        additional_subject_line: "",
      });

      // Clear any error or success messages
      setError("");
      setSuccess("");
      setFieldErrors({});
    }
  }, [rule]);

  // Effect to fetch truck sizes when RFQ sources change
  useEffect(() => {
    if (truckSizesAbortRef.current) {
      truckSizesAbortRef.current.abort();
    }

    if (formData.all_truck_types) {
      setTruckSizes([]);
      setTruckSizesError("");
      return;
    }

    // Empty rfq_sources means "all sources" — use the full active-source list.
    const effectiveSourceIds =
      formData.rfq_sources.length > 0
        ? formData.rfq_sources
        : rfqSources.map((s) => s.id);

    if (effectiveSourceIds.length === 0) {
      setTruckSizes([]);
      setTruckSizesError("");
      return;
    }

    const controller = new AbortController();
    truckSizesAbortRef.current = controller;

    const fetchTruckSizes = async () => {
      try {
        setTruckSizesLoading(true);
        setTruckSizesError("");
        const response = await rfqNotificationsService.getTruckSizesBySource(
          effectiveSourceIds,
          controller.signal,
        );
        setTruckSizes(response.truck_sizes || []);
      } catch (error) {
        if (error.name === 'AbortError' || error.code === 'ERR_CANCELED') return;
        console.error("Failed to fetch truck sizes:", error);
        setTruckSizesError("Failed to load truck types. Please try again.");
        setTruckSizes([]);
      } finally {
        setTruckSizesLoading(false);
      }
    };

    fetchTruckSizes();
  }, [formData.rfq_sources, formData.all_truck_types, rfqSources]);

  const handleInputChange = (field, value) => {
    // Safety check: if value is an event object, extract the actual value
    let safeValue = value;
    if (value && typeof value === 'object' && value.target !== undefined) {
      // This is likely an event object
      if (value.target.type === 'checkbox') {
        // For checkboxes, use checked property, not value
        safeValue = value.target.checked;
      } else {
        safeValue = value.target.value;
      }
      console.warn('handleInputChange received event object for field:', field, 'extracting value:', safeValue);
    }
    
    // Additional safety: ensure we don't store DOM elements or complex objects
    if (safeValue && typeof safeValue === 'object' && safeValue.nodeType !== undefined) {
      console.error('handleInputChange received DOM element for field:', field, 'skipping update');
      return;
    }
    
    // Special handling for miles fields to ensure they're always integers
    if (field === 'min_total_miles' || field === 'max_total_miles') {
      // If empty string or null/undefined, use default values
      if (safeValue === '' || safeValue === null || safeValue === undefined) {
        safeValue = field === 'min_total_miles' ? 0 : 99999;
      } else {
        // Convert to integer and ensure it's a valid number
        const intValue = parseInt(safeValue);
        safeValue = isNaN(intValue) ? (field === 'min_total_miles' ? 0 : 99999) : intValue;
      }
    }
    
    setFormData(prev => ({
      ...prev,
      [field]: safeValue
    }));
    
    // Special handling for time scheduling checkbox
    if (field === 'enable_time_scheduling' && safeValue === false) {
      // Clear any validation errors when disabling time scheduling
      setError("");
    }
    
    // Clear field-specific error when user starts typing
    if (fieldErrors[field]) {
      const nextFieldErrors = { ...fieldErrors, [field]: null };
      setFieldErrors(nextFieldErrors);
      // Clear the top-of-form banner ONLY when no other field still has an
      // error. Fields without an inline renderer (e.g. note_keywords_whitelist)
      // show their error solely in the banner, so blanking it while another
      // field is still invalid would hide a still-unresolved error.
      if (!Object.values(nextFieldErrors).some(Boolean)) {
        setError("");
      }
    }
  };

  // Contact selection handler
  const handleContactSelectionChange = useCallback((newSelection) => {
    // Safety check: ensure newSelection is an array of primitive values
    if (!Array.isArray(newSelection)) {
      console.error('handleContactSelectionChange received non-array:', newSelection);
      return;
    }
    
    const validIds = newSelection.filter(id => {
      // Only allow primitive values (numbers, strings)
      if (id === null || id === undefined) return false;
      if (typeof id === 'object') {
        console.warn('handleContactSelectionChange filtering out object:', id);
        return false;
      }
      return true;
    });
    
    setFormData(prev => ({
      ...prev,
      customer_contacts: validIds
    }));
    
    // Mark that user has modified contacts
    setHasUserModifiedContacts(true);
  }, []);

  // Customer selection handler
  const handleCustomerSelectionChange = useCallback((newSelection) => {
    if (!Array.isArray(newSelection)) {
      console.error('handleCustomerSelectionChange received non-array:', newSelection);
      return;
    }

    const validIds = newSelection.filter(id => {
      if (id === null || id === undefined) return false;
      if (typeof id === 'object') return false;
      return true;
    });

    setFormData(prev => ({
      ...prev,
      customers: validIds
    }));
    setHasUserModifiedCustomers(true);
  }, []);

  // Schedule management handlers
  const handleScheduleEdit = (schedule, dayIndex) => {
    setEditingSchedule(schedule);
    setEditingDayIndex(dayIndex);
    setShowScheduleModal(true);
  };

  const handleScheduleDelete = async (schedule) => {
    if (window.confirm(`Are you sure you want to remove the schedule for ${['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'][schedule.day_of_week]}?`)) {
      setScheduleLoading(true);
      setScheduleSuccess("");
      setError("");
      
      try {
        if (rule?.id && schedule.id) {
          // For existing rules, call API to delete schedule
          await dispatch(deleteSchedule(rule.id, schedule.id));
          
          // Update local state after successful API call
          const updatedSchedules = formData.schedules.filter(s => s.day_of_week !== schedule.day_of_week);
          
          // Auto-disable scheduling if this was the last schedule
          const shouldDisableScheduling = updatedSchedules.length === 0;
          
          setFormData(prev => ({
            ...prev,
            schedules: updatedSchedules,
            enable_time_scheduling: shouldDisableScheduling ? false : prev.enable_time_scheduling
          }));
        } else {
          // For new rules (no rule.id yet), just update local state
          const updatedSchedules = formData.schedules.filter(s => s.day_of_week !== schedule.day_of_week);
          
          // Auto-disable scheduling if this was the last schedule
          const shouldDisableScheduling = updatedSchedules.length === 0;
          
          setFormData(prev => ({
            ...prev,
            schedules: updatedSchedules,
            enable_time_scheduling: shouldDisableScheduling ? false : prev.enable_time_scheduling
          }));
        }
        
        const dayName = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'][schedule.day_of_week];
        const wasLastSchedule = formData.schedules.filter(s => s.day_of_week !== schedule.day_of_week).length === 0;
        
        if (wasLastSchedule) {
          setScheduleSuccess(`${dayName} schedule removed successfully! Time-based scheduling has been disabled since no schedules remain.`);
        } else {
          setScheduleSuccess(`${dayName} schedule removed successfully!`);
        }
        
        // Clear success message after 3 seconds
        setTimeout(() => setScheduleSuccess(""), 3000);
      } catch (error) {
        setError(getApiErrorMessage(error) || "Failed to remove schedule");
      } finally {
        setScheduleLoading(false);
      }
    }
  };

  const handleScheduleSave = async (scheduleData) => {
    setScheduleLoading(true);
    setScheduleSuccess("");
    setError("");
    
    try {
      const updatedSchedules = [...(formData.schedules || [])];
      
      // Determine if this is an update or create operation
      let existingIndex = -1;
      let isUpdating = false;
      
      if (scheduleData.id) {
        // If we have a schedule ID, this is definitely an update
        existingIndex = updatedSchedules.findIndex(s => s.id === scheduleData.id);
        isUpdating = existingIndex >= 0;
      } else {
        // No ID means this is a new schedule, check for day conflicts
        existingIndex = updatedSchedules.findIndex(s => s.day_of_week === scheduleData.day_of_week);
        isUpdating = false; // Always create new if no ID provided
      }
      
      let apiResponse = null;
      
      if (rule?.id) {
        // For existing rules, call API to create/update schedule
        if (isUpdating && scheduleData.id) {
          // Update existing schedule via API using the schedule ID
          apiResponse = await dispatch(updateSchedule(rule.id, scheduleData.id, scheduleData));
        } else {
          // Create new schedule via API
          apiResponse = await dispatch(createSchedule(rule.id, scheduleData));
        }
        
        // Use the API response data which includes all display fields
        const newSchedule = {
          ...apiResponse,
          day_of_week_display: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'][apiResponse.day_of_week],
          time_range_display: formatTimeRange(apiResponse.start_time, apiResponse.end_time)
        };
        
        if (isUpdating) {
          // When updating, replace the existing schedule
          updatedSchedules[existingIndex] = newSchedule;
        } else {
          // When creating new, check if there's a conflict with existing day
          const dayConflictIndex = updatedSchedules.findIndex(s => s.day_of_week === newSchedule.day_of_week);
          if (dayConflictIndex >= 0) {
            // Replace existing schedule for this day
            updatedSchedules[dayConflictIndex] = newSchedule;
          } else {
            // Add new schedule
            updatedSchedules.push(newSchedule);
          }
        }
      } else {
        // For new rules (no rule.id yet), just update local state
        const newSchedule = {
          ...scheduleData,
          day_of_week_display: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'][scheduleData.day_of_week],
          time_range_display: formatTimeRange(scheduleData.start_time, scheduleData.end_time)
        };
        
        if (isUpdating) {
          // When updating, replace the existing schedule
          updatedSchedules[existingIndex] = newSchedule;
        } else {
          // When creating new, check if there's a conflict with existing day
          const dayConflictIndex = updatedSchedules.findIndex(s => s.day_of_week === newSchedule.day_of_week);
          if (dayConflictIndex >= 0) {
            // Replace existing schedule for this day
            updatedSchedules[dayConflictIndex] = newSchedule;
          } else {
            // Add new schedule
            updatedSchedules.push(newSchedule);
          }
        }
      }
      
      setFormData(prev => ({
        ...prev,
        schedules: updatedSchedules
      }));
      
      setShowScheduleModal(false);
      setEditingSchedule(null);
      setEditingDayIndex(null);
      
      const dayName = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'][scheduleData.day_of_week];
      setScheduleSuccess(
        `${dayName} schedule ${isUpdating ? 'updated' : 'added'} successfully!`
      );
      
      // Clear success message after 3 seconds
      setTimeout(() => setScheduleSuccess(""), 3000);
    } catch (error) {
      setError(getApiErrorMessage(error) || "Failed to save schedule");
    } finally {
      setScheduleLoading(false);
    }
  };

  const formatTimeRange = (startTime, endTime) => {
    const start = startTime.substring(0, 5); // Remove seconds if present
    const end = endTime.substring(0, 5);
    
    // Check if it's an overnight schedule
    if (startTime > endTime) {
      return `${start}-${end} (next day)`;
    }
    return `${start}-${end}`;
  };

  const handleToggleStatus = async () => {
    if (!rule?.id) {
      // For new rules, just toggle locally
      handleInputChange("is_active", !formData.is_active);
      return;
    }

    // Mirror the Create/Update save gate: the toggle's PUT also sends the truck
    // slice (buildTruckPayloadFields) for permitted users, so an out-of-range
    // Max Miles Out must be blocked here with the same inline field error rather
    // than dispatched and bounced back as a generic backend 400.
    if (!validateTruckAvailability()) {
      return;
    }

    setToggleLoading(true);
    setError("");

    try {
      const updatedRule = {
        ...omitTruckFields(formData),
        // Convert empty/blank names to null
        name: formData.name && formData.name.trim() ? formData.name.trim() : null,
        is_active: !formData.is_active,
        // Email as string for API
        emails_for_sending_notification: formData.emails_for_sending_notification || '',
        pick_up_states_only: Array.isArray(formData.pick_up_states_only) 
          ? formData.pick_up_states_only.join(',') 
          : '',
        delivery_states_only: Array.isArray(formData.delivery_states_only) 
          ? formData.delivery_states_only.join(',') 
          : '',
        // Send contact IDs in customer_contacts field
        customer_contacts: Array.isArray(formData.customer_contacts) 
          ? formData.customer_contacts.filter(id => id !== null && id !== undefined)
          : [],
        // Send truck types data (matching main form submission logic)
        all_truck_types: formData.all_truck_types,
        // Truck-availability fields (only included when the user is permitted)
        ...buildTruckPayloadFields(),
        // review-note: formData.allowed_truck_types is always an array (state init [], edit-population coerces CSV string via split→array / array as-is / else []); the : '' arm is unreachable defensive fallback, not test-reachable
        allowed_truck_types: formData.all_truck_types
          ? null
          : Array.isArray(formData.allowed_truck_types)
            ? formData.allowed_truck_types.join(',')
            : ''
      };

      await dispatch(updateRfqNotificationRule(rule.id, updatedRule));
      handleInputChange("is_active", !formData.is_active);
      setSuccess(`Rule ${!formData.is_active ? 'activated' : 'deactivated'} successfully!`);
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(""), 3000);
    } catch (error) {
      setError(getApiErrorMessage(error) || "Failed to update notification rule");
    } finally {
      setToggleLoading(false);
    }
  };

  // Helper function to parse and clean CSV emails
  const parseCSVEmails = (emailString) => {
    if (!emailString || !emailString.trim()) {
      return [];
    }
    
    return emailString
      .split(',')
      .map(email => email.trim())
      .filter(email => email.length > 0);
  };

  // Updated validation function to handle multiple CSV emails
  const validateEmails = (emailString) => {
    const emails = parseCSVEmails(emailString);
    
    if (emails.length === 0) {
      return { isValid: false, message: "At least one email is required" };
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    
    for (const email of emails) {
      if (!emailRegex.test(email)) {
        return { isValid: false, message: `Invalid email format: ${email}` };
      }
      if (!email.endsWith('@uskoexpedite.com')) {
        return { isValid: false, message: `Email must be @uskoexpedite.com: ${email}` };
      }
    }
    
    return { isValid: true };
  };

  // Legacy function for backward compatibility
  const validateEmail = (email) => {
    const validation = validateEmails(email);
    return validation.isValid;
  };

  // Schedule validation functions
  const validateSchedules = () => {
    if (!formData.enable_time_scheduling) {
      return { isValid: true }; // No validation needed if scheduling is disabled
    }

    if (!formData.schedules || formData.schedules.length === 0) {
      return { 
        isValid: false, 
        message: "At least one schedule is required when time-based scheduling is enabled" 
      };
    }

    // Validate each schedule
    for (const schedule of formData.schedules) {
      // Validate time format
      if (!isValidTimeFormat(schedule.start_time)) {
        return { 
          isValid: false, 
          message: `Invalid start time format for ${schedule.day_of_week_display || 'schedule'}` 
        };
      }
      if (!isValidTimeFormat(schedule.end_time)) {
        return { 
          isValid: false, 
          message: `Invalid end time format for ${schedule.day_of_week_display || 'schedule'}` 
        };
      }

      // Check if start and end times are the same
      const start = schedule.start_time.substring(0, 5);
      const end = schedule.end_time.substring(0, 5);
      if (start === end) {
        return { 
          isValid: false, 
          message: `Start and end times cannot be the same for ${schedule.day_of_week_display || 'schedule'}` 
        };
      }

      // Validate day of week
      if (schedule.day_of_week < 0 || schedule.day_of_week > 6) {
        return { 
          isValid: false, 
          message: "Invalid day of week in schedule" 
        };
      }
    }

    // Check for duplicate days
    const daySet = new Set();
    for (const schedule of formData.schedules) {
      if (daySet.has(schedule.day_of_week)) {
        const dayNames = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
        return { 
          isValid: false, 
          message: `Duplicate schedule found for ${dayNames[schedule.day_of_week]}` 
        };
      }
      daySet.add(schedule.day_of_week);
    }

    return { isValid: true };
  };

  // Validates "Max Miles Out (air)" — required and bounded to 0-300 (inclusive)
  // only when the truck-availability filter is engaged (all active trucks, or at
  // least one specific truck selected) and the user holds the permission. Sets
  // the inline field error and returns false on failure; true otherwise.
  const validateTruckAvailability = () => {
    const truckFilterEngaged = canManageTruckAvailability &&
      (formData.all_active_trucks || (Array.isArray(formData.trucks) && formData.trucks.length > 0));

    if (!truckFilterEngaged) {
      return true;
    }

    const raw = formData.max_miles_out_air;
    if (raw === "" || raw === null || raw === undefined) {
      setError("Max Miles Out (air) is required when the truck-availability filter is engaged");
      setFieldErrors(prev => ({
        ...prev,
        max_miles_out_air: "Required — enter a whole number from 0 to 300"
      }));
      return false;
    }

    const numericValue = Number(raw);
    if (!Number.isInteger(numericValue) || numericValue < 0 || numericValue > 300) {
      setError("Max Miles Out (air) must be a whole number between 0 and 300");
      setFieldErrors(prev => ({
        ...prev,
        max_miles_out_air: "Enter a whole number from 0 to 300"
      }));
      return false;
    }

    return true;
  };

  const validateForm = () => {
    if (!formData.emails_for_sending_notification || !formData.emails_for_sending_notification.trim()) {
      setError("Notification email is required");
      return false;
    }

    const emailValidation = validateEmails(formData.emails_for_sending_notification);
    if (!emailValidation.isValid) {
      setError(emailValidation.message);
      return false;
    }

    if (formData.min_total_miles && formData.max_total_miles) {
      if (parseInt(formData.min_total_miles) >= parseInt(formData.max_total_miles)) {
        setError("Minimum miles must be less than maximum miles");
        return false;
      }
    }

    // Validate truck types
    if (!formData.all_truck_types && (!formData.allowed_truck_types || formData.allowed_truck_types.length === 0)) {
      setError("At least one truck type must be selected when 'Accept all truck types' is disabled");
      return false;
    }

    // Validate schedules
    const scheduleValidation = validateSchedules();
    if (!scheduleValidation.isValid) {
      setError(scheduleValidation.message);
      return false;
    }

    // Validate the truck-availability section (Max Miles Out, when engaged)
    if (!validateTruckAvailability()) {
      return false;
    }

    return true;
  };

  // Helper function to validate and sanitize data for JSON serialization
  const sanitizeForJson = (obj) => {
    const sanitized = {};
    
    for (const [key, value] of Object.entries(obj)) {
      // Skip DOM elements and complex objects
      if (value && typeof value === 'object' && value.nodeType !== undefined) {
        continue;
      }

      // Skip React fiber nodes and other circular references
      if (value && typeof value === 'object' && (value._owner || value.__reactFiber$)) {
        continue;
      }
      
      sanitized[key] = value;
    }
    
    return sanitized;
  };

  // Builds the truck-availability slice of the API payload. Returns an empty
  // object when the user lacks the permission so the truck fields are never
  // sent by a non-permitted user (avoids the serializer permission gate and
  // keeps non-engaged rules byte-for-byte identical to today). Used by BOTH
  // write paths (handleSubmit and the activate/deactivate toggle) so they
  // cannot drift apart.
  const buildTruckPayloadFields = () => {
    if (!canManageTruckAvailability) {
      return {};
    }

    return {
      all_active_trucks: formData.all_active_trucks,
      // review-note: formData.trucks is always an array (state init [], MultiSelect/remove onChange and edit-population sanitize all produce arrays); the : [] arm is unreachable defensive fallback, not test-reachable
      trucks: Array.isArray(formData.trucks)
        ? formData.trucks.filter(id => id !== null && id !== undefined && typeof id !== 'object')
        : [],
      max_miles_out_air: normalizeMaxMilesOutAir(formData.max_miles_out_air)
    };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    setSubmitting(true);
    setError("");
    setSuccess("");
    setFieldErrors({});

    try {
      const ruleData = sanitizeForJson({
        ...omitTruckFields(formData),
        // Convert empty/blank names to null
        name: formData.name && formData.name.trim() ? formData.name.trim() : null,
        min_total_miles: formData.min_total_miles !== null && formData.min_total_miles !== undefined && formData.min_total_miles !== "" ? parseInt(formData.min_total_miles) : 0,
        max_total_miles: formData.max_total_miles !== null && formData.max_total_miles !== undefined && formData.max_total_miles !== "" ? parseInt(formData.max_total_miles) : 99999,
        // Email as string for API
        emails_for_sending_notification: formData.emails_for_sending_notification || '',
        pick_up_states_only: Array.isArray(formData.pick_up_states_only) 
          ? formData.pick_up_states_only.join(',') 
          : '',
        delivery_states_only: Array.isArray(formData.delivery_states_only) 
          ? formData.delivery_states_only.join(',') 
          : '',
        // Send rfq_sources as array of integers, or empty array
        rfq_sources: Array.isArray(formData.rfq_sources) 
          ? formData.rfq_sources.filter(id => id !== null && id !== undefined)
          : [],
        // Send truck types data
        all_truck_types: formData.all_truck_types,
        allowed_truck_types: formData.all_truck_types 
          ? null 
          : Array.isArray(formData.allowed_truck_types) 
            ? formData.allowed_truck_types.join(',')
            : '',
        // Send customer IDs
        customers: Array.isArray(formData.customers)
          ? formData.customers.filter(id => id !== null && id !== undefined && typeof id !== 'object')
          : [],
        all_customers: formData.all_customers,
        // Truck-availability fields (only included when the user is permitted)
        ...buildTruckPayloadFields(),
        // Send contact IDs in customer_contacts field
        customer_contacts: Array.isArray(formData.customer_contacts)
          ? formData.customer_contacts.filter(id => id !== null && id !== undefined && typeof id !== 'object')
          : []
      });

      let createdOrUpdatedRule;
      
      if (rule) {
        createdOrUpdatedRule = await dispatch(updateRfqNotificationRule(rule.id, ruleData));
        setSuccess("Rule updated successfully!");
      } else {
        createdOrUpdatedRule = await dispatch(createRfqNotificationRule(ruleData));
        
        // For newly created rules, bulk create schedules if they exist
        if (formData.schedules && formData.schedules.length > 0 && createdOrUpdatedRule?.id) {
          try {
            const schedulesToCreate = formData.schedules.map(schedule => ({
              day_of_week: schedule.day_of_week,
              start_time: schedule.start_time,
              end_time: schedule.end_time
            }));
            
            await dispatch(bulkCreateSchedules(createdOrUpdatedRule.id, { schedules: schedulesToCreate }));
            setSuccess("Rule and schedules created successfully!");
          } catch (scheduleError) {
            console.error('Failed to create schedules for new rule:', scheduleError);
            const scheduleErrorMessage = getApiErrorMessage(scheduleError);
            setSuccess(
              scheduleErrorMessage
                ? `Rule created successfully, but some schedules failed to save: ${scheduleErrorMessage}`
                : "Rule created successfully, but some schedules failed to save. Please edit the rule to add schedules."
            );
          }
        } else {
          setSuccess("Rule created successfully!");
        }
      }

      setTimeout(() => {
        onSave();
      }, 1000);
    } catch (error) {
      const parsed = parseApiError(error);

      // RuleForm's local fieldErrors state is field -> single string (first
      // message per field); parseApiError's fieldErrorLists is field -> array.
      const fieldErrorsObj = {};
      Object.keys(parsed.fieldErrorLists).forEach(field => {
        fieldErrorsObj[field] = parsed.fieldErrorLists[field][0];
      });
      setFieldErrors(fieldErrorsObj);

      setError(resolveBannerMessage(parsed, Boolean(rule)));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className={styles.form}>
        <div className={styles.loading}>Loading rule details...</div>
      </div>
    );
  }

  // Check if contact panel should be displayed
  const contactsAreSame = rule?.customer_contacts && formData.customer_contacts &&
    rule.customer_contacts.length === formData.customer_contacts.length &&
    rule.customer_contacts.every(id => formData.customer_contacts.includes(id)) &&
    formData.customer_contacts.every(id => rule.customer_contacts.includes(id));

  const shouldShowContactPanel = (!rule || hasUserModifiedContacts || contactsAreSame) && !formData.all_customer_contacts;

  const customersAreSame = rule?.customers && formData.customers &&
    rule.customers.length === formData.customers.length &&
    rule.customers.every(id => formData.customers.includes(id)) &&
    formData.customers.every(id => rule.customers.includes(id));

  const shouldShowCustomerPanel = (!rule || hasUserModifiedCustomers || customersAreSame) && !formData.all_customers;

  return (
    <>
    <form onSubmit={handleSubmit} className={styles.form}>
      {/* review-note: Back and the footer Cancel deliberately share onCancel —
          the page spec requires both to return to the rules list (Back is the
          form's top-of-page affordance, Cancel the footer action). Identical
          semantics here are intentional, not a divergence. */}
      <div className={styles.backRow}>
        <Button type="button" className={styles.backButton} onClick={onCancel}>
          ← Back
        </Button>
      </div>
      <div className={styles.formHeader}>
        <h2>{rule ? "Edit Rule" : "Create New Rule"}</h2>
        <button 
          type="button" 
          className={styles.helpButton}
          onClick={() => setShowHelpModal(true)}
          title="Help Guide"
        >
          ❓
        </button>
      </div>
      
      {(error || ruleDetailsError) && (
        <div className={styles.error}>{error || ruleDetailsError}</div>
      )}
      {success && <Message text={success} />}

      <div className={styles.section}>
        <div className={styles.basicInfoHeader}>
          <h3>Basic Information</h3>
          <div className={styles.statusSection}>
            <span className={`${styles.statusBadge} ${formData.is_active ? styles.active : styles.inactive}`}>
              {formData.is_active ? "Active" : "Inactive"}
            </span>
            <button 
              type="button" 
              onClick={handleToggleStatus}
              disabled={toggleLoading}
              className={styles.toggleButton}
            >
              {toggleLoading ? "..." : (formData.is_active ? "Deactivate" : "Activate")}
            </button>
          </div>
        </div>
        <div className={styles.fieldLabel}>
          Rule Name
        </div>
        <FormField
          label="Rule Name"
          value={formData.name}
          onChange={(e) => handleInputChange("name", e.target.value)}
          placeholder="Enter rule name"
          required={false}
        />
        {fieldErrors.name && (
          <div className={styles.fieldError}>{fieldErrors.name}</div>
        )}

        <div className={styles.emailFieldGroup}>
          <div className={styles.fieldLabel}>
            Email for RFQ notifications
          </div>
          <BadgeInput
            id="emails_for_sending_notification"
            value={formData.emails_for_sending_notification}
            onChange={(csv) =>
              handleInputChange("emails_for_sending_notification", csv)
            }
            placeholder="Type an email and press Enter (e.g., user@uskoexpedite.com)"
            validateBadge={(email) => {
              const result = validateEmails(email);
              return result.isValid ? null : result.message;
            }}
            maxBadges={10}
            ariaLabel="Notification email recipients"
          />
          {fieldErrors.emails_for_sending_notification && (
            <div className={styles.fieldError}>{fieldErrors.emails_for_sending_notification}</div>
          )}
        </div>

        <div className={styles.fieldLabel}>
          Additional Subject Line
        </div>
        <FormField
          label="Additional Subject Line"
          value={formData.additional_subject_line}
          onChange={(e) => handleInputChange("additional_subject_line", e.target.value)}
          placeholder="Optional: prepend to email subject, e.g. URGENT"
          required={false}
          maxLength={255}
        />
      </div>

      <div className={styles.section}>
        <h3>Geographic Criteria</h3>
        
        <div className={styles.stateSelectors}>
          <div className={styles.multiSelectField}>
            <div className={styles.fieldLabel}>Pickup States</div>
            <MultiSelect
              options={ALL_STATES_WITH_SEPARATORS}
              value={(formData.pick_up_states_only || []).map(value => ({ value, label: value }))}
              onChange={(selected) => handleInputChange("pick_up_states_only", selected.map(item => item.value).filter(value => !value.includes('_SEPARATOR')))}
              labelledBy="Select pickup states"
              hasSelectAll={false}
              overrideStrings={{
                "selectSomeItems": "Leave empty to receive from all states"
              }}
            />
          </div>

          <div className={styles.multiSelectField}>
            <div className={styles.fieldLabel}>Delivery States</div>
            <MultiSelect
              options={ALL_STATES_WITH_SEPARATORS}
              value={(formData.delivery_states_only || []).map(value => ({ value, label: value }))}
              onChange={(selected) => handleInputChange("delivery_states_only", selected.map(item => item.value).filter(value => !value.includes('_SEPARATOR')))}
              labelledBy="Select delivery states"
              hasSelectAll={false}
              overrideStrings={{
                "selectSomeItems": "Leave empty to receive from all states"
              }}
            />
          </div>
        </div>

        <Checkbox
          label="Match both pickup and delivery states"
          checked={formData.match_pick_up_and_delivery_states}
          onChange={(checked) => handleInputChange("match_pick_up_and_delivery_states", checked)}
        />
      </div>

      <div className={styles.section}>
        <h3>Distance and Source</h3>
        
        <div className={styles.milesInputs}>
          <div className={styles.fieldLabel}>
            Minimum Miles
          </div>
          <FormField
            label="Minimum Miles"
            type="number"
            value={formData.min_total_miles}
            onChange={(e) => handleInputChange("min_total_miles", e.target.value)}
            placeholder="0"
            min="0"
          />
          {fieldErrors.min_total_miles && (
            <div className={styles.fieldError}>{fieldErrors.min_total_miles}</div>
          )}
          <div className={styles.fieldLabel}>
            Maximum Miles
          </div>
          <FormField
            label="Maximum Miles"
            type="number"
            value={formData.max_total_miles}
            onChange={(e) => handleInputChange("max_total_miles", e.target.value)}
            placeholder="99999"
            min="0"
          />
          {fieldErrors.max_total_miles && (
            <div className={styles.fieldError}>{fieldErrors.max_total_miles}</div>
          )}
        </div>

        <div className={styles.multiSelectField}>
          <div className={styles.fieldLabel}>RFQ Sources</div>
          <MultiSelect
            options={rfqSources.map(source => ({ value: source.id, label: source.name }))}
            value={(formData.rfq_sources || []).map(id => ({ value: id, label: rfqSources.find(s => s.id === id)?.name || `Source ${id}` }))}
            onChange={(selected) => handleInputChange("rfq_sources", selected.map(item => item.value))}
            labelledBy="Select RFQ sources"
            hasSelectAll={false}
            disabled={rfqSourcesLoading}
            overrideStrings={{
              "selectSomeItems": rfqSourcesLoading ? "Loading sources..." : "Leave empty to receive from all sources"
            }}
          />
          {rfqSourcesError && (
            <div className={styles.fieldError}>{rfqSourcesError}</div>
          )}
          {fieldErrors.rfq_sources && (
            <div className={styles.fieldError}>{fieldErrors.rfq_sources}</div>
          )}
        </div>
      </div>

      <div className={styles.section}>
        <h3>Truck Types</h3>
        
        <Checkbox
          label="Accept all truck types"
          value={formData.all_truck_types}
          onChange={(e) => handleInputChange("all_truck_types", e.target.checked)}
        />

        {!formData.all_truck_types && (
          <div className={styles.multiSelectField}>
            <div className={styles.fieldLabel}>Allowed Truck Types</div>
            <MultiSelect
              options={truckSizes.map(size => ({ value: size, label: size }))}
              value={(formData.allowed_truck_types || []).map(type => ({ value: type, label: type }))}
              onChange={(selected) => handleInputChange("allowed_truck_types", selected.map(item => item.value))}
              labelledBy="Select allowed truck types"
              hasSelectAll={false}
              disabled={truckSizesLoading}
              overrideStrings={{
                "selectSomeItems": truckSizesLoading
                  ? "Loading truck types..."
                  : "Select truck types to allow",
              }}
            />
            {truckSizesError && (
              <div className={styles.fieldError}>{truckSizesError}</div>
            )}
            {fieldErrors.allowed_truck_types && (
              <div className={styles.fieldError}>{fieldErrors.allowed_truck_types}</div>
            )}
          </div>
        )}
      </div>

      <div className={styles.section}>
        <h3>Load Note Keywords (Optional)</h3>

        <div className={styles.multiSelectField}>
          <div className={styles.fieldLabel}>
            Whitelist — send only if the load note contains one of these
          </div>
          <BadgeInput
            id="note_keywords_whitelist"
            value={formData.note_keywords_whitelist}
            onChange={(csv) => handleInputChange("note_keywords_whitelist", csv)}
            placeholder="Type a keyword or phrase and press Enter (e.g., dock high)"
            ariaLabel="Note keyword whitelist"
          />
        </div>

        <div className={styles.multiSelectField}>
          <div className={styles.fieldLabel}>
            Blacklist — do not send if the load note contains one of these
          </div>
          <BadgeInput
            id="note_keywords_blacklist"
            value={formData.note_keywords_blacklist}
            onChange={(csv) => handleInputChange("note_keywords_blacklist", csv)}
            placeholder="Type a keyword or phrase and press Enter (e.g., hazmat)"
            ariaLabel="Note keyword blacklist"
          />
        </div>
      </div>

      <div className={styles.section}>
        <h3>Customers</h3>

        <Checkbox
          label="All customers"
          value={formData.all_customers}
          onChange={(e) => handleInputChange("all_customers", e.target.checked)}
        />

        {shouldShowCustomerPanel && (
          <CustomerSelectionPanel
            key={`customers-${rule?.id}`}
            initialSelectedCustomers={formData.customers || []}
            onSelectionChange={handleCustomerSelectionChange}
            className={styles.contactSelectionPanel}
            sourceIds={formData.rfq_sources || []}
          />
        )}
      </div>

      <div className={styles.section}>
        <h3>Customer Contacts</h3>

        <Checkbox
          label="All customer contacts"
          value={formData.all_customer_contacts}
          onChange={(e) => handleInputChange("all_customer_contacts", e.target.checked)}
        />

        <div className={styles.contactsSection}>
          {shouldShowContactPanel && (
            <ContactSelectionPanel
              key={rule?.id}
              initialSelectedContacts={formData.customer_contacts || []}
              onSelectionChange={handleContactSelectionChange}
              autoFetchOnMount={true}
              showSelectedPanel={true}
              className={styles.contactSelectionPanel}
              sourceIds={formData.rfq_sources || []}
            />
          )}
        </div>
      </div>

      {canManageTruckAvailability && (
        <div className={styles.section}>
          <h3>Truck Availability</h3>
          <TruckAvailabilitySection
            allActiveTrucks={formData.all_active_trucks}
            selectedTruckIds={formData.trucks}
            maxMilesOutAir={formData.max_miles_out_air}
            availableTrucks={availableTrucks}
            availableTrucksLoading={availableTrucksLoading}
            availableTrucksError={availableTrucksError}
            maxMilesError={fieldErrors.max_miles_out_air}
            onChange={handleInputChange}
          />
        </div>
      )}

      <div className={styles.section}>
        <h3>Time-Based Scheduling (Optional)</h3>
        
        <Checkbox
          id="enable_time_scheduling"
          label="Enable time-based scheduling for notifications"
          value={formData.enable_time_scheduling}
          onChange={(checked) => handleInputChange("enable_time_scheduling", checked)}
        />
        
        {formData.enable_time_scheduling && (
          <div className={styles.scheduleContainer}>
            {scheduleSuccess && (
              <div className={styles.scheduleSuccess}>
                {scheduleSuccess}
              </div>
            )}

            <div className={styles.scheduleHeader}>
              <span className={styles.scheduleStatus}>
                {formData.schedules && formData.schedules.length > 0 
                  ? `${formData.schedules.length} day${formData.schedules.length !== 1 ? 's' : ''} scheduled`
                  : '24/7 notifications (no schedule set)'
                }
              </span>
              {scheduleLoading && (
                <span className={styles.scheduleLoader}>Updating...</span>
              )}
            </div>
            
            <div className={styles.scheduleGrid}>
              {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].map((day, index) => {
                const schedule = formData.schedules && formData.schedules.find(s => s.day_of_week === index);
                return (
                  <div key={day} className={styles.scheduleRow}>
                    <div className={styles.dayLabel}>{day}</div>
                    <div className={styles.timeRange}>
                      {schedule ? (
                        <span className={schedule.start_time > schedule.end_time ? styles.overnight : ''}>
                          {schedule.time_range_display}
                        </span>
                      ) : (
                        <span className={styles.notScheduled}>Not scheduled</span>
                      )}
                    </div>
                    <div className={styles.scheduleActions}>
                      <button
                        type="button"
                        className={styles.scheduleButton}
                        onClick={() => handleScheduleEdit(schedule, index)}
                        disabled={scheduleLoading}
                      >
                        {schedule ? 'Edit' : 'Add'}
                      </button>
                      {schedule && (
                        <button
                          type="button"
                          className={styles.scheduleButtonDelete}
                          onClick={() => handleScheduleDelete(schedule)}
                          disabled={scheduleLoading}
                        >
                          Delete
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      <div className={styles.actions}>
        <div className={styles.leftActions}>
          {/* Empty - keeps buttons on the right */}
        </div>
        <div className={styles.rightActions}>
          <Button type="button" onClick={onCancel} variant="outline">
            Cancel
          </Button>
          <Button type="submit" disabled={submitting}>
            {submitting ? "Saving..." : (rule ? "Update Rule" : "Create Rule")}
          </Button>
        </div>
      </div>
      
      <RuleFormHelpModal 
        isOpen={showHelpModal} 
        onClose={() => setShowHelpModal(false)} 
      />
    </form>
    
    <ScheduleModal
      isOpen={showScheduleModal}
      schedule={editingSchedule}
      dayIndex={editingDayIndex}
      existingSchedules={formData.schedules}
      onSave={handleScheduleSave}
      onClose={() => setShowScheduleModal(false)}
      loading={scheduleLoading}
    />
    </>
  );
};

export default RuleForm;